/**
 * Heartbeat de saúde do sync (`sync_heartbeat`, ≤1/h por aparelho).
 *
 * Helpers puros (testados) + `startHeartbeat()` (efeito). Não edita nenhuma zona
 * crítica: `syncQueue` / `circuitBreaker` são só LIDOS, via import dinâmico (mesmo
 * padrão de `triggerImmediatePush`, evita ciclo events.ts <-> sync).
 */
import { Q } from '@nozbe/watermelondb'
import database from '../database'
import { recordEvent } from './events'

export const HEARTBEAT_INTERVAL_MS = 60 * 60 * 1000
const CHECK_EVERY_MS = 5 * 60 * 1000
const FIRST_RUN_DELAY_MS = 10 * 1000
const LAST_AT_KEY = 'obs_heartbeat_at'

export function shouldSendHeartbeat(
  lastAt: number | null,
  now: number,
  every = HEARTBEAT_INTERVAL_MS,
) {
  return lastAt == null || now - lastAt >= every
}

export type HeartbeatSnapshot = {
  queuePending?: number
  queueOldestAt?: number | null
  deadLetter?: number
  eventsPending?: number
  eventsOldestAt?: number | null
  circuit?: {
    state?: string
    consecutiveFailures?: number
    lastSuccess?: string | null
  }
  lastSyncPull?: string | null
  storage?: { usage?: number; quota?: number }
}

export function buildHeartbeatPayload(s: HeartbeatSnapshot, now: number) {
  return {
    queuePending: s.queuePending ?? 0,
    queueOldestAgeMs: s.queueOldestAt ? now - s.queueOldestAt : null,
    deadLetter: s.deadLetter ?? 0,
    eventsPending: s.eventsPending ?? 0,
    eventsOldestAgeMs: s.eventsOldestAt ? now - s.eventsOldestAt : null,
    circuitState: s.circuit?.state ?? null,
    circuitFailures: s.circuit?.consecutiveFailures ?? 0,
    lastPushOkAt: s.circuit?.lastSuccess ?? null,
    lastSyncPull: s.lastSyncPull ?? null,
    storageUsage: s.storage?.usage ?? null,
    storageQuota: s.storage?.quota ?? null,
  }
}

// ─── Efeito ─────────────────────────────────────────────────────────────────

function readLastAt(): number | null {
  try {
    return Number(localStorage.getItem(LAST_AT_KEY)) || null
  } catch {
    return null
  }
}

// Cada fonte tem try/catch próprio: a falha de uma não derruba as outras.
async function collectSnapshot(): Promise<HeartbeatSnapshot> {
  const snap: HeartbeatSnapshot = {}

  try {
    const { syncQueue } = await import('../sync/SyncQueue')
    const stats: any = await (syncQueue as any).getStats()
    snap.queuePending = stats?.pending
    snap.queueOldestAt = stats?.oldestItem ?? null
    snap.deadLetter = stats?.deadLetter?.total
  } catch {
    /* fonte indisponível */
  }

  try {
    const { circuitBreaker } = await import('../sync/CircuitBreaker')
    // 'push' é o endpoint usado em circuitBreaker.canExecute('push') (syncWorker).
    const st: any = (circuitBreaker as any).getStatus('push')
    snap.circuit = {
      state: st?.state,
      consecutiveFailures: st?.consecutiveFailures,
      lastSuccess: st?.lastSuccess ?? null,
    }
  } catch {
    /* fonte indisponível */
  }

  try {
    snap.lastSyncPull = localStorage.getItem('lastSyncPull')
  } catch {
    /* ignore */
  }

  try {
    const collection: any = database.collections.get('events')
    const pending: any[] = await collection
      .query(Q.where('_status', 'created'))
      .fetch()
    snap.eventsPending = pending.length
    snap.eventsOldestAt = pending.length
      ? Math.min(...pending.map((e) => Number(e.timestamp) || Date.now()))
      : null
  } catch {
    /* fonte indisponível */
  }

  try {
    const est = await (navigator as any).storage?.estimate?.()
    if (est) snap.storage = { usage: est.usage, quota: est.quota }
  } catch {
    /* ignore */
  }

  return snap
}

async function tick(): Promise<void> {
  try {
    const now = Date.now()
    if (!shouldSendHeartbeat(readLastAt(), now)) return
    if (
      typeof document !== 'undefined' &&
      document.visibilityState !== 'visible'
    ) {
      return
    }

    // Só com usuário logado (import dinâmico: evita ciclo com redux/auth).
    const authMod = await import('../redux/auth/actions')
    const auth: any = await authMod.getLoggedUser()
    if (!auth?.userId) return

    const payload = buildHeartbeatPayload(await collectSnapshot(), now)
    await recordEvent({
      level: 'info',
      category: 'sync',
      action: 'sync_heartbeat',
      payload,
    })
    // Só persiste depois de gravar: falha => tenta de novo no próximo tick.
    localStorage.setItem(LAST_AT_KEY, String(now))
  } catch {
    /* falhas silenciosas */
  }
}

let started = false

export function startHeartbeat(): void {
  if (started || typeof window === 'undefined') return
  started = true
  setTimeout(() => void tick(), FIRST_RUN_DELAY_MS)
  setInterval(() => void tick(), CHECK_EVERY_MS)
}
